/**
 * src/lib/anhLogo.js — nhận file logo và thu nhỏ ngay trên trình duyệt.
 *
 * Vì sao thu nhỏ chứ không lưu nguyên file: logo người dùng chọn thường là ảnh
 * xuất từ thiết kế, 2–5 MB. Bản chuẩn bị được đồng bộ nguyên cục lên Firestore
 * (trần 1 MB/document) nên một file như vậy là hỏng đồng bộ ngay. Thu về 256px
 * là quá đủ cho header file Excel và bản tóm tắt một trang.
 *
 * Ảnh KHÔNG được gửi đi đâu để xử lý — toàn bộ chạy bằng canvas trong máy người
 * dùng, rồi nằm chung với phần còn lại của Bản chuẩn bị.
 */

import { TRAN_LOGO } from './validateWorkspace.js';

export const CANH_TOI_DA = 256;
const LOAI_NHAN = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
/** Trần file đầu vào trước khi thu nhỏ. */
export const TRAN_FILE_VAO = 5 * 1024 * 1024;

/**
 * @param {File} file
 * @returns {Promise<string>} data URL đã thu nhỏ
 */
export async function docLogo(file) {
  if (!file) throw new Error('Chưa chọn file.');
  if (!LOAI_NHAN.includes(file.type))
    throw new Error('Chỉ nhận file PNG, JPG, WEBP hoặc SVG.');
  if (file.size > TRAN_FILE_VAO)
    throw new Error('File vượt 5 MB. Hãy xuất lại logo ở kích thước nhỏ hơn.');

  const nguon = await new Promise((ok, loi) => {
    const r = new FileReader();
    r.onload = () => ok(r.result);
    r.onerror = () => loi(new Error('Không đọc được file.'));
    r.readAsDataURL(file);
  });

  const img = await new Promise((ok, loi) => {
    const i = new Image();
    i.onload = () => ok(i);
    // SVG không khai báo kích thước sẽ không vẽ được lên canvas; báo rõ thay vì
    // để người dùng nhìn ô trống mà không hiểu vì sao.
    i.onerror = () => loi(new Error('File ảnh không đọc được. Thử xuất sang PNG.'));
    i.src = nguon;
  });

  const canh = Math.max(img.width, img.height);
  if (!canh) throw new Error('File ảnh không có kích thước. Thử xuất sang PNG.');
  const ty = Math.min(1, CANH_TOI_DA / canh);
  const w = Math.max(1, Math.round(img.width * ty));
  const h = Math.max(1, Math.round(img.height * ty));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d').drawImage(img, 0, 0, w, h);

  // PNG giữ được nền trong suốt — logo đặt lên header màu khác sẽ không có ô
  // trắng quanh chữ.
  let ra = canvas.toDataURL('image/png');
  if (ra.length > TRAN_LOGO) ra = canvas.toDataURL('image/jpeg', 0.85);
  if (ra.length > TRAN_LOGO)
    throw new Error('Logo vẫn quá nặng sau khi thu nhỏ. Hãy dùng ảnh đơn giản hơn.');
  return ra;
}
